import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config';
import AdminLayout from './AdminLayout';
import './Admin.css';

function ContentManagement() {
    const [content, setContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterStatus, setFilterStatus] = useState('pending');
    const [filterType, setFilterType] = useState('all');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [selectedContent, setSelectedContent] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const { currentUser } = useAuth();

    useEffect(() => {
        fetchContent();
    }, [pagination.page, filterStatus, filterType]);

    const fetchContent = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!currentUser) {
                throw new Error('Not authenticated');
            }

            const token = await currentUser.getIdToken();
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                status: filterStatus,
                ...(filterType !== 'all' && { type: filterType })
            });

            const response = await fetch(`${API_URL}/api/admin/content?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server error: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                setContent(data.content || []);
                setPagination(prev => ({ ...prev, total: data.total || 0 }));
            } else {
                setContent([]);
            }
        } catch (err) {
            console.error('Content fetch error:', err);
            setError(err.message);
            setContent([]);
        } finally {
            setLoading(false);
        }
    };

    const handleModerate = async (contentId, action, reason = '') => {
        try {
            setActionLoading(true);
            const token = await currentUser.getIdToken();

            const response = await fetch(`${API_URL}/api/admin/content/${contentId}/moderate`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action, reason })
            });

            if (!response.ok) throw new Error('Moderation failed');

            const data = await response.json();
            if (data.success) {
                fetchContent();
                setSelectedContent(null);
            }
        } catch (err) {
            alert('Failed to moderate content: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: <span className="badge badge-warning">Pending Review</span>,
            approved: <span className="badge badge-success">Approved</span>,
            rejected: <span className="badge badge-danger">Rejected</span>,
            flagged: <span className="badge badge-danger">Flagged</span>
        };
        return badges[status] || <span className="badge">{status}</span>;
    };

    const getTypeBadge = (type) => {
        const badges = {
            lesson: <span className="badge badge-info">Lesson</span>,
            quiz: <span className="badge badge-purple">Quiz</span>,
            flashcard: <span className="badge badge-teal">Flashcard</span>,
            learning_path: <span className="badge badge-orange">Learning Path</span>
        };
        return badges[type] || <span className="badge">{type}</span>;
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp._seconds
            ? new Date(timestamp._seconds * 1000)
            : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <AdminLayout>
            <div className="admin-content">
                <div className="admin-header">
                    <h1>Content Management</h1>
                    <span className="total-count">{pagination.total} items</span>
                </div>

                {/* Filters */}
                <div className="filters-bar">
                    <div className="filter-tabs">
                        {['pending', 'approved', 'rejected', 'flagged', 'all'].map(status => (
                            <button
                                key={status}
                                className={`filter-tab ${filterStatus === status ? 'active' : ''}`}
                                onClick={() => {
                                    setFilterStatus(status);
                                    setPagination(p => ({ ...p, page: 1 }));
                                }}
                            >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </button>
                        ))}
                    </div>

                    <select
                        value={filterType}
                        onChange={(e) => {
                            setFilterType(e.target.value);
                            setPagination(p => ({ ...p, page: 1 }));
                        }}
                        className="filter-select"
                    >
                        <option value="all">All Types</option>
                        <option value="lesson">Lessons</option>
                        <option value="quiz">Quizzes</option>
                        <option value="flashcard">Flashcards</option>
                        <option value="learning_path">Learning Paths</option>
                    </select>
                </div>

                {/* Content Grid */}
                {loading ? (
                    <div className="admin-loading-container">
                        <div className="spinner"></div>
                        <p>Loading content...</p>
                    </div>
                ) : error ? (
                    <div className="admin-error">
                        <p>{error}</p>
                        <button onClick={fetchContent}>Retry</button>
                    </div>
                ) : content.length === 0 ? (
                    <div className="empty-state">
                        <span className="empty-icon">📭</span>
                        <h3>No content found</h3>
                        <p>No content matches your current filters.</p>
                    </div>
                ) : (
                    <>
                        <div className="content-grid">
                            {content.map(item => (
                                <div key={item.id} className="content-card">
                                    <div className="content-card-header">
                                        {getTypeBadge(item.type)}
                                        {getStatusBadge(item.status)}
                                    </div>

                                    <h3 className="content-title">{item.title || item.topic}</h3>

                                    <p className="content-preview">
                                        {item.preview || item.content?.substring(0, 150) + '...'}
                                    </p>

                                    <div className="content-meta">
                                        <span>Created: {formatDate(item.createdAt)}</span>
                                        {item.reportCount > 0 && (
                                            <span className="report-count">
                                                ⚠️ {item.reportCount} reports
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        className="review-btn"
                                        onClick={() => setSelectedContent(item)}
                                    >
                                        Review Content
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="pagination">
                            <button
                                disabled={pagination.page <= 1}
                                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                            >
                                Previous
                            </button>
                            <span>Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit) || 1}</span>
                            <button
                                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}

                {/* Content Review Modal */}
                {selectedContent && (
                    <div className="modal-overlay" onClick={() => setSelectedContent(null)}>
                        <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Review Content</h2>
                                <button className="close-btn" onClick={() => setSelectedContent(null)}>×</button>
                            </div>

                            <div className="modal-body">
                                <div className="review-header">
                                    {getTypeBadge(selectedContent.type)}
                                    {getStatusBadge(selectedContent.status)}
                                </div>

                                <h3>{selectedContent.title || selectedContent.topic}</h3>

                                <div className="content-full-preview">
                                    <h4>Content Preview</h4>
                                    <div className="preview-box">
                                        {typeof selectedContent.content === 'object'
                                            ? JSON.stringify(selectedContent.content, null, 2)
                                            : selectedContent.content || 'No content available'
                                        }
                                    </div>
                                </div>

                                {selectedContent.reports && selectedContent.reports.length > 0 && (
                                    <div className="reports-section">
                                        <h4>User Reports</h4>
                                        {selectedContent.reports.map((report, idx) => (
                                            <div key={idx} className="report-item">
                                                <span className="report-reason">{report.reason}</span>
                                                <span className="report-date">{formatDate(report.timestamp)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="content-metadata">
                                    <div className="meta-item">
                                        <label>Content ID</label>
                                        <span>{selectedContent.id}</span>
                                    </div>
                                    <div className="meta-item">
                                        <label>Created</label>
                                        <span>{formatDate(selectedContent.createdAt)}</span>
                                    </div>
                                    <div className="meta-item">
                                        <label>Topic</label>
                                        <span>{selectedContent.topic}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-actions">
                                {selectedContent.status !== 'approved' && (
                                    <button
                                        className="action-btn success"
                                        onClick={() => handleModerate(selectedContent.id, 'approve')}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? 'Processing...' : 'Approve'}
                                    </button>
                                )}
                                {selectedContent.status !== 'rejected' && (
                                    <button
                                        className="action-btn danger"
                                        onClick={() => {
                                            const reason = prompt('Reason for rejection:');
                                            if (reason) handleModerate(selectedContent.id, 'reject', reason);
                                        }}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? 'Processing...' : 'Reject'}
                                    </button>
                                )}
                                <button
                                    className="action-btn warning"
                                    onClick={() => handleModerate(selectedContent.id, 'flag')}
                                    disabled={actionLoading}
                                >
                                    Flag for Review
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

export default ContentManagement;
